import {
  BarChart3,
  BookOpen,
  Brain,
  CheckCircle2,
  Download,
  FileText,
  Flame,
  Gauge,
  Library,
  LockKeyhole,
  MessageSquare,
  RotateCcw,
  Scale,
  Settings,
  ShieldCheck,
  Target,
  Trophy,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { AdminImportPanel } from "./components/AdminImportPanel";
import { BottomNav } from "./components/BottomNav";
import { CasePanel } from "./components/CasePanel";
import { CaseSwitcher } from "./components/CaseSwitcher";
import { FeedbackPanel } from "./components/FeedbackPanel";
import { HomePanel } from "./components/HomePanel";
import { Onboarding } from "./components/Onboarding";
import { OverviewPanel } from "./components/OverviewPanel";
import { PracticePanel } from "./components/PracticePanel";
import { RunSummary } from "./components/RunSummary";
import { SettingsPanel } from "./components/SettingsPanel";
import { cases } from "./data/cases";
import { exercises, learningPaths, validateQuestionBank } from "./data/questions";
import { vibrate } from "./lib/haptics";
import { analyticsSummary, attemptsToday, filterForMode, milestoneFor, recommendNext } from "./lib/learningEngine";
import { createSoundService, getSharedAudioContext } from "./lib/sound";
import { summarizeRun, type RunSummary as RunSummaryData } from "./lib/runSummary";
import { submitFeedback } from "./platform/sync";
import { useLearningState } from "./state/useLearningState";
import { usePreferences } from "./state/usePreferences";
import { APP_MARK, APP_NAME, APP_STAGE, IS_ADMIN } from "./config";
import type { LearningPathId, LearningState, ModeId } from "./types";

const modeTabs: Array<{ id: ModeId; label: string; icon: LucideIcon }> = [
  { id: "daily", label: "Daily Drill", icon: Target },
  { id: "case", label: "Case Gym", icon: Brain },
  { id: "claim", label: "Claim Lab", icon: Scale },
  { id: "exam", label: "Exam Mode", icon: Gauge },
];

const navItems = [
  { id: "home", label: "Start", icon: Trophy, adminOnly: false },
  { id: "train", label: "Trainieren", icon: Target, adminOnly: false },
  { id: "overview", label: "Übersicht", icon: BarChart3, adminOnly: false },
  { id: "feedback", label: "Fehler melden", icon: MessageSquare, adminOnly: false },
  { id: "settings", label: "Einstellungen", icon: Settings, adminOnly: false },
  { id: "library", label: "Bibliothek", icon: Library, adminOnly: true },
  { id: "admin", label: "Import", icon: FileText, adminOnly: true },
  { id: "beta", label: "Beta", icon: LockKeyhole, adminOnly: true },
] as const;

const visibleNavItems = navItems.filter((item) => IS_ADMIN || !item.adminOnly);
type SectionId = (typeof navItems)[number]["id"] | "summary";

function pathLabel(pathId: LearningPathId | "all") {
  if (pathId === "all") return "Alle Pfade";
  return learningPaths.find((path) => path.id === pathId)?.label ?? pathId;
}

function ringBucket(value: number) {
  return Math.min(100, Math.max(0, Math.round(value / 5) * 5));
}

function displayMilestone(text: string) {
  return text.replace("Good Learning Time", "Fokuszeit");
}

function DossierRail({
  state,
  summary,
  dailyGoalItems,
  pathId,
  onDetails,
}: {
  state: LearningState;
  summary: ReturnType<typeof analyticsSummary>;
  dailyGoalItems: number;
  pathId: LearningPathId | "all";
  onDetails: () => void;
}) {
  const doneToday = attemptsToday(state);
  const cards = Object.values(state.cards);
  const masteryScore = cards.length
    ? Math.round((cards.reduce((sum, card) => sum + card.mastery, 0) / (cards.length * 5)) * 100)
    : Math.max(12, summary.accuracy);
  const dailyTarget = Math.max(1, dailyGoalItems);
  const dailyPct = Math.min(100, Math.round((Math.min(doneToday, dailyTarget) / dailyTarget) * 100));
  const ringClass = `ring-pct-${ringBucket(masteryScore)}`;
  const rows = [
    { label: "Neuheit", value: Math.max(18, Math.min(100, masteryScore)) },
    { label: "Erfinderische Taetigkeit", value: Math.max(14, summary.accuracy) },
    { label: "Gewerbliche Anwendbarkeit", value: Math.min(100, Math.max(20, summary.activeSkills * 12)) },
  ];

  return (
    <aside className="dossier-rail" aria-label="Meisterung und Tagesziel">
      <div className="rail-section rail-mastery">
        <span className="eyebrow">Meisterung</span>
        <div
          aria-label={`Gesamtfortschritt ${Math.min(100, masteryScore)} Prozent`}
          aria-valuemax={100}
          aria-valuemin={0}
          aria-valuenow={Math.min(100, masteryScore)}
          className={`dossier-ring ${ringClass}`}
          role="progressbar"
        >
          <strong>{Math.min(100, masteryScore)}%</strong>
          <span>Gesamtfortschritt</span>
        </div>
      </div>

      <div className="rail-section">
        <span className="eyebrow">Tagesziel</span>
        <strong className="rail-number">
          {Math.min(doneToday, dailyTarget)}/{dailyTarget}
        </strong>
        <span className="rail-caption">Fragen heute</span>
        <meter max="100" value={dailyPct} />
      </div>

      <div className="rail-section">
        <span className="eyebrow">Streak</span>
        <div className="streak-stat">
          <Flame size={19} />
          <strong>{summary.streak}</strong>
        </div>
        <span className="rail-caption">Tage in Folge</span>
      </div>

      <div className="rail-section">
        <span className="eyebrow">Kompetenzen</span>
        <div className="rail-competencies">
          {rows.map((row) => (
            <div className="rail-topic" key={row.label}>
              <div>
                <span>{row.label}</span>
                <b>{row.value}%</b>
              </div>
              <meter max="100" value={row.value} />
            </div>
          ))}
        </div>
      </div>

      <button className="rail-detail-button" onClick={onDetails} type="button">
        <BookOpen size={16} />
        Details anzeigen
      </button>

      <div className="rail-footer">
        <span>{pathLabel(pathId)}</span>
      </div>
    </aside>
  );
}

// Sichtbarer Session-Timer fuer den Exam Mode (Zeitbox-Charakter).
function ExamTimer() {
  const [start] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const secs = Math.floor((now - start) / 1000);
  const mm = String(Math.floor(secs / 60)).padStart(2, "0");
  const ss = String(secs % 60).padStart(2, "0");
  return (
    <span className="exam-timer">
      ⏱ {mm}:{ss}
    </span>
  );
}

export function App() {
  const { state, acceptBetaRules, submitAttempt, addImportJob, exportProgress, resetProgress } = useLearningState();
  const { prefs, update: updatePrefs } = usePreferences();
  const soundService = useMemo(
    () =>
      createSoundService({
        getContext: getSharedAudioContext,
        isEnabled: () => prefs.sound,
        getVolume: () => prefs.soundVolume,
      }),
    [prefs.sound, prefs.soundVolume]
  );
  const [activeSection, setActiveSection] = useState<SectionId>("home");
  const [mode, setMode] = useState<ModeId>("daily");
  const [pathId, setPathId] = useState<LearningPathId | "all">("all");
  const [lastResult, setLastResult] = useState<string>("Bereit fuer die naechste gute Session.");
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [runBefore, setRunBefore] = useState<LearningState | null>(null);
  const [runSummary, setRunSummary] = useState<RunSummaryData | null>(null);
  const [sessionIntro, setSessionIntro] = useState(false);
  const [toastShownThisRun, setToastShownThisRun] = useState(false);

  const summary = useMemo(() => analyticsSummary(state, exercises), [state]);
  const validationIssues = useMemo(() => validateQuestionBank(exercises), []);
  const availableCases = useMemo(
    () => (pathId === "all" ? cases : cases.filter((caseExercise) => caseExercise.pathId === pathId)),
    [pathId]
  );
  const activeCase = availableCases.find((caseExercise) => caseExercise.id === selectedCaseId) ?? availableCases[0];
  const modeAvailability = useMemo(
    () =>
      Object.fromEntries(
        modeTabs.map((tab) => {
          if (tab.id === "case") {
            const forPath = pathId === "all" ? cases : cases.filter((caseEx) => caseEx.pathId === pathId);
            return [tab.id, forPath.length];
          }
          return [tab.id, filterForMode(exercises, tab.id, pathId, state).length];
        })
      ) as Record<ModeId, number>,
    [pathId, state]
  );

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!sessionIntro) return;
    const timer = setTimeout(() => setSessionIntro(false), 800);
    return () => clearTimeout(timer);
  }, [sessionIntro]);

  function startRun(nextPathId: LearningPathId | "all") {
    setRunBefore(state);
    setRunSummary(null);
    setToastShownThisRun(false);
    setPathId(nextPathId);
    setMode("daily");
    setSessionIntro(true);
    setActiveSection("train");
  }

  function completeRun() {
    const before = runBefore ?? state;
    setRunSummary(summarizeRun(before, state, exercises, pathId, learningPaths));
    setActiveSection("summary");
  }

  return (
    <div className={`app-shell ${activeSection === "train" ? "is-session" : ""}`}>
      <aside className="sidebar" aria-label="Lernplattform Navigation">
        <a className="brand" href="#top">
          <span className="brand-mark">{APP_MARK}</span>
          <span>
            <strong>{APP_NAME}</strong>
            <small>{APP_STAGE}</small>
          </span>
        </a>
        <nav>
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                className={activeSection === item.id ? "is-active" : ""}
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                type="button"
              >
                <Icon size={18} />
                {item.label}
              </button>
            );
          })}
        </nav>
        <div className="private-badge">
          <ShieldCheck size={18} />
          <span>Private Beta · noindex · Access-Gate</span>
        </div>
      </aside>

      <main id="top">
        <header className="topbar">
          <div className="topbar-copy">
            <span className="eyebrow">Graschtat IP Training</span>
            <h1>Executive Dossier</h1>
          </div>
          <div className="topbar-actions">
            <button className="ghost-action" onClick={exportProgress} type="button">
              <Download size={17} />
              Export
            </button>
            <button className="ghost-action danger" onClick={resetProgress} type="button">
              <RotateCcw size={17} />
              Reset
            </button>
          </div>
        </header>

        {!prefs.onboardedAt ? (
          <Onboarding
            paths={learningPaths}
            onComplete={(patch) => updatePrefs(patch)}
            onStartRun={startRun}
          />
        ) : null}

        {prefs.onboardedAt && activeSection === "home" ? (
          <HomePanel
            dailyGoalItems={prefs.dailyGoalItems}
            exercises={exercises}
            onStartRun={startRun}
            paths={learningPaths}
            state={state}
          />
        ) : null}

        {prefs.onboardedAt && activeSection === "summary" && runSummary ? (
          <RunSummary
            summary={runSummary}
            onContinue={() => {
              const next = recommendNext(state, exercises, learningPaths);
              const nextPath = (next?.pathId as LearningPathId | undefined) ?? learningPaths[0]?.id;
              if (nextPath) startRun(nextPath);
            }}
            onClose={() => setActiveSection("home")}
          />
        ) : null}

        {prefs.onboardedAt && activeSection === "train" ? (
          <>
            {sessionIntro ? <div className="session-intro">Run startet...</div> : null}
            <div className="training-dossier-grid">
              <section className="training-dossier-main">
                <section className="train-bar">
              <div className="train-bar-meta">
                <span className="eyebrow">
                  {pathLabel(pathId)} · {modeTabs.find((tab) => tab.id === mode)?.label}
                  {mode === "exam" ? (
                    <>
                      {" · "}
                      <ExamTimer />
                    </>
                  ) : null}
                </span>
                    <span className="train-glt">Fokuszeit heute: {summary.goodLearningMinutes}m</span>
              </div>
              <p className="train-signal">
                <Trophy size={15} /> {lastResult}
              </p>
                </section>

                <div className="train-paths" role="group" aria-label="Lernpfad">
              <button className={`chip ${pathId === "all" ? "is-active" : ""}`} onClick={() => setPathId("all")} type="button">
                Alle Pfade
              </button>
              {learningPaths.map((path) => (
                <button
                  className={`chip ${pathId === path.id ? "is-active" : ""}`}
                  key={path.id}
                  onClick={() => setPathId(path.id)}
                  type="button"
                >
                  {path.label}
                </button>
              ))}
                </div>

                <div className="mode-tabs" aria-label="Lernmodi">
              {modeTabs.map((tab) => {
                const Icon = tab.icon;
                const count = modeAvailability[tab.id];
                return (
                  <button
                    className={mode === tab.id ? "is-active" : ""}
                    key={tab.id}
                    onClick={() => setMode(tab.id)}
                    type="button"
                    disabled={count === 0}
                    title={count === 0 ? "Noch kein Material - kommt mit der Content-Pipeline" : `${count} verfuegbar`}
                  >
                    <Icon size={18} />
                    {tab.label} · {count}
                  </button>
                );
              })}
                </div>

                <div className="workspace-single">
              {mode === "case" && activeCase ? (
                <div className="case-column">
                  {availableCases.length > 1 ? (
                    <CaseSwitcher cases={availableCases} activeId={activeCase.id} onSelect={setSelectedCaseId} />
                  ) : null}
                  <CasePanel
                    caseExercise={activeCase}
                    hapticsEnabled={prefs.haptics}
                    key={activeCase.id}
                    soundService={soundService}
                    state={state}
                    onAnswer={(stepExercise, m, selected, elapsedSeconds, difficulty) => {
                      const result = submitAttempt(stepExercise, m, selected, elapsedSeconds, undefined, difficulty);
                      setLastResult(result?.correct ? "Schritt sitzt." : "Guter Fehler: Schritt wird wiederholt.");
                      const milestone = result ? milestoneFor(state, result.state, exercises) : null;
                      if (milestone && !toastShownThisRun) {
                        setToast(displayMilestone(milestone));
                        setToastShownThisRun(true);
                      }
                    }}
                  />
                </div>
              ) : (
                    <PracticePanel
                      exercises={exercises}
                      mode={mode}
                      onSubmit={(exercise, selected, elapsedSeconds, grade, difficulty) => {
                    const result = submitAttempt(exercise, mode, selected, elapsedSeconds, grade, difficulty);
                    setLastResult(result?.correct ? "Kompetenzpunkt gesichert." : "Guter Fehler: wird frueher wiederholt.");
                    const milestone = result ? milestoneFor(state, result.state, exercises) : null;
                    if (milestone && !toastShownThisRun) {
                      setToast(displayMilestone(milestone));
                      setToastShownThisRun(true);
                      if (milestone.toLowerCase().includes("stabil")) {
                        soundService.playCue("stable");
                        vibrate([12, 24, 16], prefs.haptics);
                      }
                    }
                      }}
                      pathId={pathId}
                      hapticsEnabled={prefs.haptics}
                      onRunComplete={completeRun}
                      soundService={soundService}
                      state={state}
                    />
              )}
                </div>
              </section>
              <DossierRail
                state={state}
                summary={summary}
                dailyGoalItems={prefs.dailyGoalItems}
                pathId={pathId}
                onDetails={() => setActiveSection("overview")}
              />
            </div>
          </>
        ) : null}

        {prefs.onboardedAt && activeSection === "overview" ? (
          <OverviewPanel
            state={state}
            exercises={exercises}
            paths={learningPaths}
            onStartLearning={startRun}
          />
        ) : null}

        {prefs.onboardedAt && activeSection === "feedback" ? (
          <FeedbackPanel onSubmit={(message) => submitFeedback(import.meta.env.VITE_API_BASE, message)} />
        ) : null}

        {prefs.onboardedAt && activeSection === "settings" ? <SettingsPanel prefs={prefs} onChange={updatePrefs} /> : null}

        {prefs.onboardedAt && activeSection === "library" ? (
          <section className="library-panel">
            <div className="panel-heading">
              <span className="eyebrow">Lernbibliothek</span>
              <h2>Quellenbasierter Content, Review-Status und Skill-Mapping.</h2>
            </div>
            <div className="library-grid">
              {learningPaths.map((path) => {
                const pathExercises = exercises.filter((exercise) => exercise.pathId === path.id);
                const pathIssues = validationIssues.filter((issue) => pathExercises.some((exercise) => exercise.id === issue.id));
                return (
                  <article className="library-card" key={path.id}>
                    <div className="library-card-head">
                      <span className={`library-path-dot path-accent-${path.id}`} />
                      <h3>{path.label}</h3>
                    </div>
                    <p>{path.description}</p>
                    <dl>
                      <div>
                        <dt>Fragen</dt>
                        <dd>{pathExercises.length}</dd>
                      </div>
                      <div>
                        <dt>Quellen</dt>
                        <dd>{new Set(pathExercises.flatMap((exercise) => exercise.sourceIds)).size}</dd>
                      </div>
                      <div>
                        <dt>Issues</dt>
                        <dd>{pathIssues.length}</dd>
                      </div>
                    </dl>
                  </article>
                );
              })}
            </div>
            <div className="issue-panel">
              <h3>Question Validator</h3>
              {validationIssues.length ? (
                <ul>
                  {validationIssues.slice(0, 12).map((issue) => (
                    <li className={issue.severity} key={`${issue.id}-${issue.message}`}>
                      {issue.id}: {issue.message}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>
                  <CheckCircle2 size={17} />
                  Alle Beta-Fragen bestehen die strukturellen Checks.
                </p>
              )}
            </div>
          </section>
        ) : null}

        {prefs.onboardedAt && activeSection === "admin" ? <AdminImportPanel jobs={state.importJobs} onCreate={addImportJob} /> : null}

        {prefs.onboardedAt && activeSection === "beta" ? (
          <section className="beta-panel">
            <div className="panel-heading">
              <span className="eyebrow">Private Beta Gate</span>
              <h2>Geschuetzte Subdomain, leichte Regeln, volle Analyse.</h2>
            </div>
            <div className="beta-grid">
              <article>
                <LockKeyhole size={22} />
                <h3>Zugang</h3>
                <p>Vorgesehen: lernen.graschtat-ip.de hinter Cloudflare Access OTP, keine oeffentliche Navigation.</p>
              </article>
              <article>
                <ShieldCheck size={22} />
                <h3>Regeln</h3>
                <p>Keine Weitergabe, keine Rechtsberatung, Feedbackzweck, Datenexport und Loeschmoeglichkeit.</p>
              </article>
              <article>
                <BarChart3 size={22} />
                <h3>Analyse</h3>
                <p>Retention, Sessiondauer, Fokuszeit, Meisterung, Abbruchpunkte und Fehlerarten.</p>
              </article>
            </div>
            <button className="primary-action" onClick={acceptBetaRules} type="button">
              <CheckCircle2 size={18} />
              Beta-Regeln akzeptieren
            </button>
            {state.betaRulesAcceptedAt ? (
              <p className="accepted-note">Akzeptiert am {new Date(state.betaRulesAcceptedAt).toLocaleString("de-DE")}</p>
            ) : null}
          </section>
        ) : null}
      </main>
      {prefs.onboardedAt ? <BottomNav activeId={activeSection} onSelect={(id) => setActiveSection(id)} /> : null}
      {toast ? (
        <div className="toast" role="status">
          {toast}
        </div>
      ) : null}
    </div>
  );
}
